import { useEffect, useState } from 'react';
import { Col, Row } from "reactstrap";
import LineChart from '../common/charts/LineChart';
import BarChart from '../common/charts/BarChart';
import PieChart from '../common/charts/PieChart';
import BoxPlot from '../common/charts/BoxPlotChart';
import HistogramChart from '../common/charts/HistogramChart';
import ScatterPlot from '../common/charts/ScatterPlotChart';
import { showToaster, toasterTypes } from '../../widgets';
import { getAiAnalysisData } from './apiController';
import HorizontalClusterBarChart from '../common/charts/HorizonatClusterBarChart';
import StackedBarChart from '../common/charts/StackedBarchart';
import HorizontalBarChart from '../common/charts/HorizontalBarchart';
import MultiLineChart from '../common/charts/MultilineChart';
import ParetoChart from '../common/charts/ParetoChart';
import ErrorBarChart from '../common/charts/ErrorBarChart';
import QQPlot from '../common/charts/QQChart';
import StemAndLeafChart from '../common/charts/SteamAndLeafChart';

// Helper to check array validity
const isValidArray = arr => Array.isArray(arr) && arr.length > 0;

const ChartComponent = ({ chartData, key }) => {

    const transformData = () => {
        const { chart_type } = chartData;
        const data = chartData;

        // BAR chart
        if (chart_type === "bar" || chart_type === "horizontalbarchart") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets)
            ) return null;
            const { labels, datasets } = data.categorical_data;

            // 1. Compute total across all datasets
            const total = datasets.reduce((sum: number, ds: any) => {
                return sum + ds.data.reduce((s: number, v: number) => s + v, 0);
            }, 0);

            // 2. Build formatted objects with percentages
            const transformed: any[] = [];
            datasets.forEach((ds: any) => {
                ds.data.forEach((val: number, idx: number) => {
                    transformed.push({
                        variable: `${ds.label} - ${labels[idx]}`, // e.g. "2024 - Jan"
                        frequency: String(val),
                        percentage: ((val / total) * 100).toFixed(2),
                        error: 0
                    });
                });
            });

            return transformed;
        }

        if (chart_type === "errorbar") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets)
            ) return null;
            const { labels, datasets } = data.categorical_data;

            const transformed: any = labels.map((label, index) => {
                const mainData = datasets[0].data[index];
                const errorPlus = datasets[0].error_values[index].plus;
                const errorMinus = datasets[0].error_values[index].minus;

                return {
                    variable: label,
                    percentage: mainData,
                    error: Math.max(errorPlus, errorMinus) // or (errorPlus + errorMinus) / 2 if you want average
                }
            });

            return transformed;

        }

        // CLUSTER BAR chart
        if (chart_type === "clusterBarchart") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets)
            ) return null;
            const { labels, datasets } = data.categorical_data;

            const transformed: any[] = [];
            datasets.forEach((ds: any) => {
                const groupName = ds.label;
                const values: any = [];
                ds.data.forEach((val: number, idx: number) => {
                    values.push({
                        name: labels[idx],
                        value: val
                    })
                });
                transformed.push({
                    group: groupName,
                    values: values
                });
            });
            return transformed;
        }

        if (chart_type === "stackedbarchart") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets)
            ) return null;
            const { labels, datasets } = data.categorical_data;

            const transformed: any[] = [];

            datasets.forEach((ds: any) => {
                const label = ds.label;
                const tempData: any = { label };
                ds.data.forEach((val: number, idx: number) => {
                    tempData[labels[idx]] = val;
                });
                transformed.push(tempData);
            });

            return transformed;
        }

        if (chart_type === "multilinechart") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets)
            ) return null;
            const { labels, datasets } = data.categorical_data;

            const transformed: any[] = [];

            datasets.forEach((ds: any) => {
                const x = ds.label;
                const tempData: any = { x };
                ds.data.forEach((val: number, idx: number) => {
                    tempData[labels[idx]] = val;
                });
                transformed.push(tempData);
            });

            return transformed;
        }

        // LINE chart
        if (chart_type === "line") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets)
            ) return null;
            const { labels, datasets } = data.categorical_data;

            return labels.map((label: string, idx: number) => {
                const obj: any = { label };
                datasets.forEach((ds: any) => {
                    obj[ds.label] = ds.data[idx];
                });
                return obj;
            });
        }

        // PIE chart
        if (chart_type === "pie") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets) ||
                !isValidArray(data.categorical_data.datasets[0]?.data)
            ) return null;
            const { labels, datasets } = data.categorical_data;
            const values = datasets[0].data; // pie usually has 1 dataset

            const total = values.reduce((sum: number, v: number) => sum + v, 0);

            const transformed: any = values.map((val: number, idx: number) => ({
                variable: labels[idx],
                frequency: String(val),
                percentage: ((val / total) * 100).toFixed(2),
                error: 0
            }));

            return transformed;
        }

        if (chart_type === "paretochart") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets) ||
                !isValidArray(data.categorical_data.datasets[0]?.data)
            ) return null;
            const { labels, datasets } = data.categorical_data;

            const transformed: any = labels.map((label, index) => ({
                label,
                value: datasets[0].data[index]
            }));

            return transformed;
        }

        if (chart_type === "qqplot") {
            if (
                !data?.coordinate_data ||
                !isValidArray(data.coordinate_data.labels) ||
                !isValidArray(data.coordinate_data.datasets)
            ) return null;
            const { labels, datasets } = data.coordinate_data;
            const transformed: any = datasets.flatMap(group => group.data.map(point => point.y));

            return transformed;
        }

        if (chart_type === "stemleaf") {
            if (
                !data?.stem_and_leaf_data ||
                !isValidArray(data.stem_and_leaf_data.stems) ||
                !isValidArray(data.stem_and_leaf_data.leaves)
            ) return null;
            const { stems, leaves, unit } = data.stem_and_leaf_data;

            const transformed: any = stems.flatMap((stem, index) => {
                const leafValues = leaves[index].split(" ").map(Number);
                return leafValues.map(leaf => Number(stem) * 10 + leaf);
            });

            return transformed;
        }

        // BOXPLOT
        if (chart_type === "boxplot") {
            if (!data?.box_plot_data?.stats || !isValidArray(data.box_plot_data.stats)) return null;

            const formattedData = data.box_plot_data.stats.map(stat => {
                const outliers = stat.outliers || [];
                return [stat.min, stat.q1, stat.median, stat.q3, stat.max, ...outliers];
            });

            return formattedData;

        }

        // HISTOGRAM
        if (chart_type === "histogram") {
            if (
                !data?.categorical_data ||
                !isValidArray(data.categorical_data.labels) ||
                !isValidArray(data.categorical_data.datasets) ||
                !isValidArray(data.categorical_data.datasets[0]?.data)
            ) return null;
            // Extract labels and values from categorical_data
            const { labels, datasets } = data.categorical_data;
            const values = datasets[0].data; // assuming only one dataset

            // HistogramChart expects numeric array, so return just the values
            return values;
        }

        // SCATTER
        if (chart_type === "scatterplot") {
            const scatterData = data.coordinate_data?.datasets?.[0]?.data;
            if (!isValidArray(scatterData)) return null;

            // Return as-is since ScatterPlot expects [{x, y}, ...]
            return scatterData.map(point => ({
                x: point.x,
                y: point.y
            }));
        }

        return [];
    };

    const formattedData = transformData();

    // Dynamically pick chart component
    const renderChart = () => {
        if (!formattedData || (Array.isArray(formattedData) && formattedData.length === 0)) {
            return (
                <div className="chart-placeholder">
                    <span className="chart-placeholder-text">
                        Data not available for this chart.
                    </span>
                </div>
            );
        }
        switch (chartData.chart_type) {
            case "bar":
                return <BarChart
                    containerid={"Bar" + key}
                    data={formattedData}
                    height={300}
                    interval={10}
                    barstyle={{ color: "blue", hover: "#ccc" }}
                    bardatastyle={{ fontsize: "12px", fontweight: "500", color: "#000" }}
                    gridcolor="#ccc"
                    gridstatus="hidden"
                    yaxistext={chartData.y_axis_label}
                    yaxislabelstyle={{ fontsize: "12px", fontweight: "200", color: "red" }}
                    yaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000" }}
                    xaxistext={chartData.x_axis_label}
                    xaxislabelstyle={{ fontsize: "10px", fontweight: "400", color: "red", rotate: "-20" }}
                    xaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000" }}
                    title={chartData.title}
                    titlecolor={{ fontsize: "12px", fontweight: "600", color: "#000" }}
                />;
            case "clusterBarchart":
                return (
                    <HorizontalClusterBarChart
                        containerid={"HoriCluster" + key}
                        data={formattedData}
                        width={500}
                        height={300}
                        barstyle={{ color: "blue", hover: "#ccc" }}
                        bardatastyle={{ fontsize: "12px", fontweight: "500", color: "#000" }}
                        gridcolor="#ccc"
                        gridstatus="hidden"
                        yaxistext={chartData.y_axis_label}
                        yaxislabelstyle={{ fontsize: "12px", fontweight: "200", color: "#000", dy: "-1em" }}
                        yaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000", rotate: "-45" }}
                        xaxistext={chartData.x_axis_label}
                        xaxislabelstyle={{ fontsize: "10px", fontweight: "400", color: "#000", rotate: "-20" }}
                        xaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000", dy: "4em" }}
                        title={""}
                        titlecolor={{ fontsize: "12px", fontweight: "600", color: "#000" }}
                    />
                );
            case "horizontalbarchart":
                return (<HorizontalBarChart
                    containerid={"HoriBar" + key}
                    data={formattedData}
                    width={1000}
                    height={500}
                    barstyle={{ color: "#80cbc4", hover: "#ccc" }}
                    bardatastyle={{ fontsize: "16px", fontweight: "500", color: "red", visible: "visible" }}
                    gridcolor={"#cc1"}
                    gridstatus={"visible"}
                    yaxistext={chartData.y_axis_label}
                    yaxislabelstyle={{ fontsize: "18px", fontweight: "400", color: "#000", rotate: "-10" }}
                    yaxisstyle={{ fontsize: "18px", fontweight: "600", color: "#000" }}
                    xaxistext={chartData.x_axis_label}
                    xaxislabelstyle={{ fontsize: "18px", fontweight: "400", color: "#000", rotate: "-45" }}
                    xaxisstyle={{ fontsize: "18px", fontweight: "500", color: "#000", dy: "6em" }}
                    title={"Income Chart"}
                    titlecolor={{ fontsize: "24px", fontweight: "600", color: "green" }}
                />);
            case "stackedbarchart":
                return (
                    <StackedBarChart
                        containerid={"Stacked" + key}
                        data={formattedData}
                        width={500}
                        height={300}
                        categories={chartData.categorical_data.labels}
                        barstyle={{ color: "#80cbc4", hover: "#ccc" }}
                        bardatastyle={{ fontsize: "14px", fontweight: "500", color: "red", visible: "visible" }}
                        gridcolor={"#cc1"}
                        gridstatus={"visible"}
                        yaxistext={chartData.y_axis_label}
                        yaxislabelstyle={{ fontsize: "12px", fontweight: "400", color: "#000" }}
                        yaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000", dy: "2em" }}
                        xaxistext={chartData.x_axis_label}
                        xaxislabelstyle={{ fontsize: "12px", fontweight: "400", color: "red", rotate: "-45" }}
                        xaxisstyle={{ fontsize: "12px", fontweight: "500", color: "#000", dy: "3em" }}
                        title={" "}
                        titlecolor={{ fontsize: "24px", fontweight: "600", color: "green" }} />);
            case "pie":
                return <PieChart
                    containerid={"Pie" + key}
                    data={formattedData}
                    height={400}
                />;
            case "line":
                return (
                    <LineChart
                        containerid={"Line" + key}
                        datachart={formattedData}
                        width={500}
                        heigth={300}
                        scaley={20}
                        isarray={true}
                        chartstyle={{
                            color: "royalblue",
                            hover: "#ccc",
                            linecolor: "#cc5",
                            radius: 4
                        }}
                        gridcolor="#ccc"
                        gridstatus="hidden"
                        yaxistext={chartData.y_axis_label}
                        yaxisstyle={{
                            fontsize: "14px",
                            fontweight: "600",
                            color: "#000",
                            dy: "-2em"
                        }}
                        xaxistext={chartData.x_axis_label}
                        xaxisstyle={{
                            fontsize: "18px",
                            fontweight: "500",
                            color: "#000",
                            dy: "2em"
                        }}
                        title={chartData.title}
                        titlecolor="#000"
                        xaxistitlecolor="#000"
                        yaxistitlecolor="#000"
                    />
                );
            case "multilinechart":
                return (
                    <MultiLineChart
                        containerid={"MultiLine" + key}
                        data={formattedData}
                        width={300}
                        height={100}
                        yrange={0}
                        chartstyle={{ color: "royalblue", hover: "#ccc", linecolor: "#cc5", radius: 4 }}
                        chartdatastyle={{ fontsize: "16px", fontweight: "500", color: "black", visible: "visible" }}
                        gridcolor={"#ccc"}
                        gridstatus={"visible"}
                        yaxistext={"Life Exp Y label"}
                        yaxislabelstyle={{ fontsize: "18px", fontweight: "400", color: "#000" }}
                        yaxisstyle={{ fontsize: "14px", fontweight: "600", color: "#000", dy: "-2em" }}
                        xaxistext={"Income per capital values "}
                        xaxislabelstyle={{ fontsize: "18px", fontweight: "400", color: "#000", rotate: "-45" }}
                        xaxisstyle={{ fontsize: "18px", fontweight: "500", color: "#000", dy: "2em" }}
                        title={"Income Chart"}
                        titlecolor={{ fontsize: "24px", fontweight: "600", color: "green" }} />
                );
            case "boxplot":
                return (
                    <BoxPlot
                        containerid={"Boxplot" + key}
                        data={formattedData}
                        label={chartData.box_plot_data?.labels} // categories shown on x-axis
                        width={500}
                        height={300}
                        chartstyle={{ color: "lightblue", hover: "#ccc", dotradius: 5, linecolor: "#ccc" }}
                        chartdatastyle={{ fontsize: "12px", fontweight: "500", color: "black", visible: "visible" }}
                        chartdots={{ color: "#000", radius: 0 }}
                        gridcolor={"#ccc"}
                        gridstatus={"visible"}
                        yaxistext={chartData?.y_axis_label}
                        yaxislabelstyle={{ fontsize: "14px", fontweight: "400", color: "#000" }}
                        yaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000", dy: "-2em" }}
                        xaxistext={chartData?.x_axis_label}
                        xaxislabelstyle={{ fontsize: "12px", fontweight: "500", color: "#000", rotate: "-45" }}
                        xaxisstyle={{ fontsize: "14px", fontweight: "500", color: "#000", dy: "0em" }}
                        title={chartData.title}
                        titlecolor={{ fontsize: "24px", fontweight: "600", color: "green" }}
                    />
                );
            case "histogram":
                return (
                    <HistogramChart
                        containerid={"Histogram" + key}
                        data={formattedData}
                        width={500}
                        height={300}
                        title={chartData.title}
                    />
                );
            case "scatterplot":
                return (
                    <ScatterPlot
                        containerid={"Scatter" + key}
                        data={formattedData}
                        width={500}
                        height={300}
                        chartstyle={{
                            color: "red",
                            hover: "#ccc",
                            dotradius: 4,
                            linecolor: "transparent"
                        }}
                        chartdatastyle={{
                            fontsize: "14px",
                            fontweight: "500",
                            color: "black",
                            visible: "visible"
                        }}
                        gridcolor={"#ccc"}
                        gridstatus={"visible"}
                        yaxistext={chartData?.y_axis_label}
                        yaxislabelstyle={{
                            fontsize: "16px",
                            fontweight: "400",
                            color: "#000"
                        }}
                        yaxisstyle={{
                            fontsize: "14px",
                            fontweight: "600",
                            color: "#000",
                            dy: "-2em"
                        }}
                        xaxistext={chartData?.x_axis_label}
                        xaxislabelstyle={{
                            fontsize: "16px",
                            fontweight: "400",
                            color: "red",
                            rotate: "-45"
                        }}
                        xaxisstyle={{
                            fontsize: "14px",
                            fontweight: "500",
                            color: "#000",
                            dy: "2em"
                        }}
                        title={chartData?.title || "Scatter Plot"}
                        titlecolor={{
                            fontsize: "20px",
                            fontweight: "600",
                            color: "green"
                        }}
                    />
                );
            case "paretochart":
                return (
                    <ParetoChart
                        containerid={"Pareto" + key}
                        data={formattedData}
                        width={400}
                        height={300}
                        gridcolor={"#ccc"}
                        gridstatus={"visible"}
                        barstyle={{ color: "orange", hover: "#ccc" }}
                        bardatastyle={{ fontsize: "12px", fontweight: "500", color: "black", visible: "visible" }}
                        linestyle={{ color: "blue", hover: "#ccc" }}
                        linedatastyle={{ fontsize: "12px", fontweight: "500", color: "black", radius: 3, visible: "visible" }}
                        title={" "}
                        titlecolor={{ fontsize: "24px", fontweight: "600", color: "green" }}
                        yaxistext={chartData.y_axis_label}
                        yaxislabelstyle={{ fontsize: "14px", fontweight: "400", color: "#000" }}
                        yaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000", dy: "-3em" }}
                        xaxistext={chartData.x_axis_label}
                        xaxislabelstyle={{ fontsize: "14px", fontweight: "400", color: "#000", rotate: "-45" }}
                        xaxisstyle={{ fontsize: "18px", fontweight: "500", color: "#000", dy: "3em" }}
                    />
                );
            case "errorbar":
                return (
                    <ErrorBarChart
                        containerid={"Errorbar" + key}
                        data={formattedData}
                        width={500}
                        height={300}
                        interval={10}
                        barstyle={{ color: "blue", hover: "#ccc" }}
                        bardatastyle={{ fontsize: "12px", fontweight: "500", color: "#000" }}
                        gridcolor="#ccc"
                        gridstatus="hidden"
                        yaxistext={chartData.y_axis_label}
                        yaxislabelstyle={{ fontsize: "12px", fontweight: "200", color: "red" }}
                        yaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000" }}
                        xaxistext={chartData.x_axis_label}
                        xaxislabelstyle={{ fontsize: "10px", fontweight: "400", color: "red", rotate: "-20" }}
                        xaxisstyle={{ fontsize: "12px", fontweight: "600", color: "#000" }}
                        title={""}
                        titlecolor={{ fontsize: "12px", fontweight: "600", color: "#000" }}
                    />
                );
            case "qqplot":
                return (
                    <QQPlot
                        containerid={"QQPlot" + key}
                        data={formattedData}
                        width={500}
                        height={300}
                    />
                );
            case "stemleaf":
                return (
                    <StemAndLeafChart containerid={"Stem" + key} data={formattedData} width={400} height={300} />
                );
            default:
                return (
                    <div className="chart-placeholder">
                        <span className="chart-placeholder-text">
                            {chartData.chart_type.toUpperCase()} Chart: {chartData.title}
                        </span>
                    </div>
                );
        }
    };

    return (
        <div className="chart-wrapper">
            <h4 className="chart-title">{chartData.title}</h4>
            {renderChart()}
            {/* {chartData.x_axis_label && chartData.y_axis_label && (
                <p className="chart-axis-labels">
                    X-Axis: {chartData.x_axis_label} | Y-Axis: {chartData.y_axis_label}
                </p>
            )} */}
        </div>
    );
};


const TableComponent = ({ tableData }) => {

    if (
        !tableData ||
        !isValidArray(tableData.header_rows) ||
        !isValidArray(tableData.rows)
    ) {
        return (
            <div className="table-placeholder">
                <span className="table-placeholder-text">
                    Data not available for this table.
                </span>
            </div>
        );
    }

    return (
        <div className="table-wrapper">
            <h4 className="table-title">{tableData.title}</h4>
            <div className="table-scroll-container">
                <table className="data-table">
                    <thead>
                        {tableData.header_rows.map((headerRow, rowIndex) => (
                            <tr key={rowIndex} className="table-header-row">
                                {headerRow.cells.map((headerCell, cellIndex) => (
                                    <th
                                        key={cellIndex}
                                        className="table-header"
                                        colSpan={headerCell.colspan || 1}
                                    >
                                        {headerCell.label}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {tableData.rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className="table-row">
                                {row.cells.filter(Boolean).map((cell, colIndex) => (
                                    <td key={colIndex} className="table-cell">
                                        {cell?.value === null || cell?.value === undefined
                                            ? "-"
                                            : typeof cell.value === "number"
                                                ? cell.value.toLocaleString()
                                                : cell.value}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const AiAnalysisReport = (props) => {
    // const data = sampleAiAnalysisData;
    const [analysisData, setAnalysisData] = useState<any>([]);
    const [message, setMessage] = useState<string>("")
    useEffect(() => {
        const aiAnalysisStatus = JSON.parse(localStorage.getItem("ai_analysis_status") || "{}");
        if (aiAnalysisStatus?.status === "initiated" && aiAnalysisStatus?.id === props?.selectedItem?.id) {
            const currentTime = new Date().getTime();
            const timeDiff = currentTime - aiAnalysisStatus?.timestamp;
            if (timeDiff < 15 * 60 * 1000) {
                getAnalysisData("r");
                setMessage("AI analysis is in progress. Please check back after some time.");
                return;
            } else {
                localStorage.removeItem("ai_analysis_status");
                getAnalysisData();
            }
        } else {
            getAnalysisData();
        }
    }, [])

    const getAnalysisData = async (flag = "") => {
        try {
            const response = await getAiAnalysisData(props?.formData?.id, props?.projectData?.TopicID, props?.selectedItem?.id, flag);
            if (response?.status && response?.data) {
                setAnalysisData(response?.data);
                setMessage("");
            } else {
                localStorage.setItem("ai_analysis_status", JSON.stringify({ status: "initiated", id: props?.selectedItem?.id, timestamp: new Date().getTime() }))
                setMessage(response?.message)
            }
        } catch (error) {
            console.log(error);
            showToaster(toasterTypes.ERROR, error?.response?.data?.message || 'Something went wrong');
        }
    }

    return (
        <div className="ai-analysis-report-container">
            {/* Header Container */}
            <div className="header-container">
                <Row className='m-0'>
                    <Col className='pl-3 mt-3' md={9}>
                        <div className='form-name'>
                            <img className='profile-image' alt='Profile' src={`${process.env.PUBLIC_URL}/images/logo.png`} />
                            AI Data Analysis
                        </div>
                    </Col>
                    <Col className='pr-4 mt-4 text-right'>
                        <i className='icon icon-close' onClick={() => props.onClose && props.onClose()} />
                    </Col>
                </Row>
            </div>

            {/* Report Body */}
            <div className="ai-analysis-body">
                <div className='ai-analysis-note'>
                    <p>If you have made changes in the configuration or added new data please check back later ,analysis will be updated soon reflecting those changes</p>
                </div>
                {message && <div className='ai-analysis-message'>
                    <p>{message}</p>
                </div>}
                {/* Report Title and Summary */}
                <div className="report-header">
                    <h1 className="report-title">{analysisData?.title}</h1>
                    <p className="report-summary">{analysisData?.summary}</p>
                </div>

                {/* Sections */}
                {analysisData?.sections && analysisData?.sections?.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="report-section">
                        {/* Section Title */}
                        <h2 className="section-title">{section.title}</h2>

                        {/* Section Content */}
                        <p className="section-content">{section.content}</p>

                        {/* Charts */}
                        {section.charts && section.charts.length > 0 && (
                            <div className="charts-container">
                                <h3 className="subsection-title">Charts</h3>
                                {section.charts.map((chart, chartIndex) => (
                                    <ChartComponent key={chartIndex} chartData={chart} />
                                ))}
                            </div>
                        )}

                        {/* Tables */}
                        {section.tables && section.tables.length > 0 && (
                            <div className="tables-container">
                                <h3 className="subsection-title">Data Tables</h3>
                                {section.tables.map((table, tableIndex) => (
                                    <TableComponent key={tableIndex} tableData={table} />
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AiAnalysisReport;